import { c as _c } from "react/compiler-runtime";
import { getHostedBusinessOwnersOptions, getHostedUserAuthMethodsOptions, getHostedUserTokenOptions, postHostedBusinessVaultDecryptOptions } from '@onefootprint/fetch-hosted';
import type { GetHostedUserAuthMethodsResponse, GetHostedUserTokenResponse, HostedBusinessOwner, ModernRawBusinessDataRequest, OnboardingRequirementCollectBusinessData } from '@onefootprint/request-types';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { retryOnConnectionError } from '@/idv2/components/errors/get-error-key';
import { filterSensitiveBusinessDis } from '@/idv2/features/collect-business-data/utils/filter-sensitive-business-dis';
import { getAllAttributes } from '@/idv2/features/collect-business-data/utils/get-all-attributes';
import { getBusinessVaultFields } from '@/idv2/features/collect-business-data/utils/get-business-vault-fields';
import { filterBootstrapData } from '@/idv2/utils/filter-bootstrap-data';
import getDevice, { type DeviceInfo } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
const {
  logInfo
} = getLogger({
  name: 'idv2',
  location: 'collect-business-data'
});
const shouldShowManageBosStep = (attributes: string[]): boolean => {
  return attributes.includes('business_kyced_beneficial_owners');
};
const isBasicBusinessAttribute = (attr: string): boolean => attr !== 'business_kyced_beneficial_owners' && !attr.startsWith('business_beneficial_owner');
const hasBasicDataToCollect = (requirement: OnboardingRequirementCollectBusinessData): boolean => {
  return requirement.missingAttributes.some(isBasicBusinessAttribute) || requirement.recollectAttributes.some(isBasicBusinessAttribute) || requirement.populatedAttributes.some(isBasicBusinessAttribute);
};
const isLayoutRoutePath = (pathname: string, token: string) => {
  return pathname === `/onboarding/${token}/collect-business-data` || pathname === `/onboarding/${token}/collect-business-data/`;
};
export const Route = createFileRoute('/onboarding/$token/collect-business-data')({
  component: CollectBusinessDataLayout,
  beforeLoad: async ({
    params: {
      token
    },
    context: {
      queryClient,
      currentRequirement,
      kybBootstrapData
    },
    location
  }) => {
    if (currentRequirement?.kind !== 'collect_business_data') {
      throw new Error(`Invalid requirement kind: ${currentRequirement?.kind ?? 'undefined'}`);
    }
    logInfo('Collect business data layout beforeLoad started', {
      requirementKind: currentRequirement.kind,
      pathname: location.pathname.replace(token, '$token')
    });
    const headers = {
      'X-Fp-Authorization': token
    };

    // Get only the fields needed based on requirement attributes, filtering out sensitive fields
    const allFields = getBusinessVaultFields(currentRequirement);
    const decryptableFields = filterSensitiveBusinessDis(allFields);
    const allAttributes = getAllAttributes(currentRequirement);
    const needsManageBos = shouldShowManageBosStep(allAttributes);
    const needsBasicData = hasBasicDataToCollect(currentRequirement);
    // Determine which child route to show if we're at the layout route
    const isLayoutRoute = isLayoutRoutePath(location.pathname, token);
    logInfo('Collect business data attributes analysis', {
      allAttributes: allAttributes.join(','),
      needsManageBos,
      needsBasicData
    });

    // Fetch fresh data when entering via parent route, use cached data for child routes.
    // This prevents duplicate requests during redirects while ensuring fresh data on initial load.
    const decryptOptions = {
      ...postHostedBusinessVaultDecryptOptions({
        headers,
        body: {
          fields: decryptableFields
        }
      }),
      retry: retryOnConnectionError
    };
    const vaultResponse = isLayoutRoute ? await queryClient.fetchQuery(decryptOptions) : await queryClient.ensureQueryData(decryptOptions);

    // Mark tin as scrubbed if it's in populatedAttributes (exists but can't be decrypted)
    if (currentRequirement.populatedAttributes.includes('business_tin')) {
      vaultResponse['business.tin'] = 'scrubbed';
    }

    // Filter KYB bootstrap data to only include fields collected by the playbook
    const {
      filteredData: filteredBootstrapData,
      bootstrapFields
    } = filterBootstrapData(kybBootstrapData, allFields);

    // Merge filtered bootstrap data with decrypted vault (bootstrap takes precedence)
    const decryptedVault: ModernRawBusinessDataRequest = {
      ...vaultResponse,
      ...filteredBootstrapData
    };

    // Fetch business owners if needed
    // Use same strategy: fresh data on parent route, cached data on child routes
    let businessOwners: HostedBusinessOwner[] = [];
    if (needsManageBos) {
      const bosOptions = getHostedBusinessOwnersOptions({
        headers
      });
      businessOwners = isLayoutRoute ? await queryClient.fetchQuery(bosOptions) : await queryClient.ensureQueryData(bosOptions);
      logInfo('Fetched business owners', {
        count: businessOwners.length
      });
    }

    // Fetch device info and step-up auth data for TIN reveal
    const [deviceInfo, userToken, authMethods] = await Promise.all([getDevice(), queryClient.ensureQueryData({
      ...getHostedUserTokenOptions({
        headers
      }),
      retry: retryOnConnectionError
    }), queryClient.ensureQueryData(getHostedUserAuthMethodsOptions({
      headers
    }))]);

    // Build step-up context for TIN reveal
    const stepUpContext = {
      userToken: userToken as GetHostedUserTokenResponse,
      authMethods: authMethods as GetHostedUserAuthMethodsResponse,
      deviceInfo: deviceInfo as DeviceInfo
    };
    logInfo('Collect business data beforeLoad completed', {
      fieldsCount: Object.keys(decryptedVault).length,
      bosCount: businessOwners.length,
      bootstrapFields: bootstrapFields.join(','),
      hasSupportForWebauthn: deviceInfo.hasSupportForWebauthn
    });
    if (isLayoutRoute && needsBasicData) {
      logInfo('Redirecting to business-information', {
        needsBasicData
      });
      throw redirect({
        to: '/onboarding/$token/collect-business-data/business-information',
        params: {
          token
        }
      });
    }
    if (isLayoutRoute && needsManageBos) {
      logInfo('Redirecting to business-owners');
      throw redirect({
        to: '/onboarding/$token/collect-business-data/business-owners',
        params: {
          token
        }
      });
    }
    return {
      currentRequirement,
      decryptedVault,
      businessOwners,
      needsManageBos,
      needsBasicData,
      bootstrapFields,
      stepUpContext
    };
  }
});
function CollectBusinessDataLayout() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Outlet />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}